import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TRoutes } from '../../../../../shared/const'

import styles from './expertsFlow.module.scss'

/**
 * Unterpunkt „Kurzanleitung" zu den Wirtschaftsexpert:innen.
 *
 * Die Erklaerung stand frueher unter der Expert:innenliste im selben Reiter und
 * wurde dort kaum gelesen. Sie ist aber der Grund, warum es an dieser Stelle
 * keinen Knopf gibt: Die Verwaltung kann niemanden zuweisen. Deshalb steht sie
 * fuer sich, als Ablauf mit Fortschrittslinie — vier Schritte, die man auf
 * einen Blick abzaehlen kann, statt eines Absatzes Fliesstext.
 *
 * **Es gibt genau zwei Wege** (Festlegung Peters, 08.08.2026): eine bestehende
 * Expert:in anfragen oder eine neue Person einladen. Der frueher hier als
 * „Weg 2 · Veroeffentlichter Bedarf" gefuehrte Punkt ist keiner davon — ein
 * ausgeschriebener Unterstuetzungsbedarf gehoert der Verwaltung und nicht dem
 * Weg einer einzelnen Junior Company zu ihrer Expert:in.
 *
 * Die Schritte folgen dem Handbuch (Unterstuetzer:innen, Abschnitt 8 und 11).
 */

const RequestIcon = () => (
	<svg
		className={styles.stepIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M22 2 11 13" />
		<path d="M22 2 15 22l-4-9-9-4Z" />
	</svg>
)

const BellIcon = () => (
	<svg
		className={styles.stepIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
		<path d="M13.7 21a2 2 0 0 1-3.4 0" />
	</svg>
)

const DecisionIcon = () => (
	<svg
		className={styles.stepIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		{/* Gabelung: annehmen oder ablehnen */}
		<path d="M12 21v-9" />
		<path d="M12 12 5 5" />
		<path d="M5 9.5V5h4.5" />
		<path d="m12 12 7-7" />
		<path d="M19 9.5V5h-4.5" />
	</svg>
)

const LinkedIcon = () => (
	<svg
		className={styles.stepIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
		<path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
	</svg>
)

const LockIcon = () => (
	<svg
		className={styles.cardIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="3" y="11" width="18" height="11" rx="2" />
		<path d="M7 11V7a5 5 0 0 1 10 0v4" />
	</svg>
)

const UserPlusIcon = () => (
	<svg
		className={styles.cardIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
		<circle cx="9" cy="7" r="4" />
		<path d="M19 8v6" />
		<path d="M22 11h-6" />
	</svg>
)

const STEPS = [
	{ key: 'request', title: 'Expert step request title', text: 'Expert step request', Icon: RequestIcon },
	{ key: 'notified', title: 'Expert step notified title', text: 'Expert step notified', Icon: BellIcon },
	{ key: 'decision', title: 'Expert step decision title', text: 'Expert step decision', Icon: DecisionIcon },
	{ key: 'connected', title: 'Expert step connected title', text: 'Expert step connected', Icon: LinkedIcon }
]

export default function ExpertsFlowTab() {
	const { t } = useTranslation()

	return (
		<div className={styles.root}>
			<section className={styles.intro}>
				<div className={styles.introHead}>
					<h3 className={styles.introTitle}>{t('How a company gets an expert')}</h3>
					<span className={styles.introBadge}>
						<LockIcon />
						{t('Expert flow badge')}
					</span>
				</div>
				<p className={styles.introText}>{t('Expert connection intro')}</p>
			</section>

			<section className={styles.routeBlock}>
				<h4 className={styles.routeTitle}>{t('Expert flow route one')}</h4>

				{/*
				 * Die Linie liegt hinter den Kreisen (siehe .marker::before). Auf
				 * schmalen Ansichten laeuft sie senkrecht, ab 1280 px waagrecht —
				 * dieselbe Auszeichnung, nur ein anderes Raster.
				 */}
				<ol className={styles.track}>
					{STEPS.map((step, index) => (
						<li key={step.key} className={styles.step}>
							<div className={styles.marker}>
								<span className={styles.node}>{index + 1}</span>
							</div>
							<div className={styles.stepBody}>
								<span className={styles.stepHead}>
									<step.Icon />
									<span className={styles.stepTitle}>{t(step.title)}</span>
								</span>
								<p className={styles.stepText}>{t(step.text)}</p>
							</div>
						</li>
					))}
				</ol>
			</section>

			{/* Beide Wege stehen gleichrangig untereinander — der zweite ist kein
			    Nachsatz zum ersten. */}
			<section className={styles.routeBlock}>
				<h4 className={styles.routeTitle}>{t('Expert flow new people')}</h4>
				<div className={styles.cards}>
					<article className={styles.card}>
						<span className={styles.cardHead}>
							<UserPlusIcon />
						</span>
						<p className={styles.cardText}>
							{t('Expert invite anyone')}{' '}
							<Link className={styles.inlineLink} to={TRoutes.SUPPORTERS}>
								{t('supporters')}
							</Link>
							{'.'}
						</p>
					</article>
				</div>
			</section>
		</div>
	)
}
